'use client'

import classNames from 'classnames'
import { useTranslation } from 'app/i18n/client'
import ArrowLeftIcon from 'public/icons/arrow-left-pagination.svg'
import ArrowRightIcon from 'public/icons/arrow-right-pagination.svg'

interface PaginationProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
}

const MAX_VISIBLE_PAGES = 5

const getPageNumbers = (current: number, total: number): number[] => {
  if (total <= 1) return []

  if (total <= MAX_VISIBLE_PAGES || current <= 3) {
    const end = Math.min(MAX_VISIBLE_PAGES, total)
    return Array.from({ length: end }, (_, i) => i + 1)
  }

  if (current >= total - 2) {
    return Array.from(
      { length: MAX_VISIBLE_PAGES },
      (_, i) => total - MAX_VISIBLE_PAGES + 1 + i,
    )
  }

  return Array.from({ length: MAX_VISIBLE_PAGES }, (_, i) => current - 2 + i)
}

const Pagination: React.FunctionComponent<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
}) => {
  const { t } = useTranslation()

  if (totalPages <= 1) return null

  const pages = getPageNumbers(currentPage, totalPages)

  return (
    <div className="flex items-center justify-center gap-1 mt-6">
      <button
        type="button"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className={classNames(
          'flex items-center gap-2 px-3 py-1 text-sm font-medium whitespace-nowrap',
          currentPage === 1
            ? 'text-gray-150 cursor-not-allowed'
            : 'text-primary hover:bg-primary-light cursor-pointer',
        )}
      >
        <ArrowLeftIcon className="fill-current" />
        {t('Prev')}
      </button>

      {pages.map((page) => (
        <button
          type="button"
          key={page}
          onClick={() => onPageChange(page)}
          className={classNames(
            'w-8 h-8 text-sm text-primary',
            page === currentPage
              ? 'border border-primary'
              : ' hover:bg-primary-light cursor-pointer',
          )}
        >
          {page}
        </button>
      ))}

      <button
        type="button"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className={classNames(
          'flex items-center gap-2 px-3 py-1 text-sm font-medium whitespace-nowrap',
          currentPage === totalPages
            ? 'text-gray-150 cursor-not-allowed'
            : 'text-primary hover:bg-primary-light cursor-pointer',
        )}
      >
        {t('Next')}
        <ArrowRightIcon className="fill-current" />
      </button>
    </div>
  )
}

export default Pagination
